/**
 * Утилита для кэширования DOM элементов и оптимизации обновлений
 */

/**
 * Создаёт менеджер кэша DOM элементов
 */
export function createDOMCacheManager({ documentRef = document } = {}) {
  const elementCache = new Map();
  const valueCache = new Map();

  /**
   * Получает элемент из кэша или DOM
   * @param {string} selector - CSS селектор
   * @param {HTMLElement} root - Корневой элемент для поиска
   * @returns {HTMLElement|null}
   */
  function getCachedElement(selector, root = documentRef) {
    const cacheKey = `${root === documentRef ? 'doc' : root.id || 'root'}:${selector}`;
    
    if (elementCache.has(cacheKey)) {
      const element = elementCache.get(cacheKey);
      // Проверяем, что элемент всё ещё в DOM
      if (element && root.contains(element)) {
        return element;
      } else {
        // Элемент удалён из DOM, удаляем из кэша
        elementCache.delete(cacheKey);
      }
    }

    const element = root.querySelector(selector);
    if (element) {
      elementCache.set(cacheKey, element);
    }
    return element;
  }

  /**
   * Обновляет текст элемента только если значение изменилось
   * @param {string} selector - CSS селектор
   * @param {string} newValue - Новое значение
   * @param {HTMLElement} root - Корневой элемент
   * @returns {boolean} - Было ли обновление
   */
  function updateText(selector, newValue, root = documentRef) {
    const element = getCachedElement(selector, root);
    if (!element) return false;

    const cacheKey = `text:${selector}`;
    const lastValue = valueCache.get(cacheKey);

    if (lastValue !== newValue) {
      element.textContent = newValue;
      valueCache.set(cacheKey, newValue);
      return true;
    }

    return false;
  }

  /**
   * Обновляет HTML элемента только если значение изменилось
   * @param {string} selector - CSS селектор
   * @param {string} newHTML - Новый HTML
   * @param {HTMLElement} root - Корневой элемент
   * @returns {boolean} - Было ли обновление
   */
  function updateHTML(selector, newHTML, root = documentRef) {
    const element = getCachedElement(selector, root);
    if (!element) return false;

    const cacheKey = `html:${selector}`;
    const lastValue = valueCache.get(cacheKey);

    if (lastValue !== newHTML) {
      element.innerHTML = newHTML;
      valueCache.set(cacheKey, newHTML);
      return true;
    }

    return false;
  }

  /**
   * Обновляет атрибут элемента только если значение изменилось
   * @param {string} selector - CSS селектор
   * @param {string} attribute - Имя атрибута
   * @param {string} newValue - Новое значение
   * @param {HTMLElement} root - Корневой элемент
   * @returns {boolean} - Было ли обновление
   */
  function updateAttribute(selector, attribute, newValue, root = documentRef) {
    const element = getCachedElement(selector, root);
    if (!element) return false;

    const cacheKey = `attr:${selector}:${attribute}`;
    const lastValue = valueCache.get(cacheKey);

    if (lastValue !== newValue) {
      element.setAttribute(attribute, newValue);
      valueCache.set(cacheKey, newValue);
      return true;
    }

    return false;
  }

  /**
   * Обновляет стиль элемента только если значение изменилось
   * @param {string} selector - CSS селектор
   * @param {string} property - CSS свойство
   * @param {string} newValue - Новое значение
   * @param {HTMLElement} root - Корневой элемент
   * @returns {boolean} - Было ли обновление
   */
  function updateStyle(selector, property, newValue, root = documentRef) {
    const element = getCachedElement(selector, root);
    if (!element) return false;

    const cacheKey = `style:${selector}:${property}`;
    const lastValue = valueCache.get(cacheKey);

    if (lastValue !== newValue) {
      element.style[property] = newValue;
      valueCache.set(cacheKey, newValue);
      return true;
    }

    return false;
  }

  /**
   * Очищает кэш
   */
  function clearCache() {
    elementCache.clear();
    valueCache.clear();
  }

  /**
   * Очищает кэш для конкретного селектора
   */
  function clearCacheFor(selector) {
    const keysToDelete = [];
    for (const key of elementCache.keys()) {
      if (key.includes(selector)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => elementCache.delete(key));

    const valueKeysToDelete = [];
    for (const key of valueCache.keys()) {
      if (key.includes(selector)) {
        valueKeysToDelete.push(key);
      }
    }
    valueKeysToDelete.forEach(key => valueCache.delete(key));
  }

  return {
    getCachedElement,
    updateText,
    updateHTML,
    updateAttribute,
    updateStyle,
    clearCache,
    clearCacheFor
  };
}








